/**
 * Base API Client Module
 * Handles authentication, base axios configuration, and core API operations
 * Extracted from csvImportApiClient.js for better maintainability
 */

import axios from 'axios';
import mockDataService from '../mockDataService';

// Environment configuration
const API_MODE = process.env.REACT_APP_API_MODE || 'mock';
const API_BASE_URL = process.env.REACT_APP_SHOPWARE_BASE_URL || 'http://localhost:8000';
const API_URL = process.env.REACT_APP_SHOPWARE_API_URL || `${API_BASE_URL}/api`;
const USE_MOCK_DATA = API_MODE === 'mock';

// OAuth Configuration
const OAUTH_CONFIG = {
    clientId: process.env.REACT_APP_SHOPWARE_CLIENT_ID,
    clientSecret: process.env.REACT_APP_SHOPWARE_CLIENT_SECRET,
    username: process.env.REACT_APP_SHOPWARE_USERNAME,
    password: process.env.REACT_APP_SHOPWARE_PASSWORD,
};

// Debug logging
const DEBUG_API = process.env.REACT_APP_DEBUG_API === 'true';
const LOG_REQUESTS = process.env.REACT_APP_LOG_REQUESTS === 'true';

class BaseApiClient {
    constructor() {
        this.accessToken = null;
        this.tokenExpiry = null;
        this.isAuthenticating = false;
        
        this.api = axios.create({
            baseURL: API_URL,
            headers: {
                'Content-Type': 'application/json',
            },
            timeout: parseInt(process.env.REACT_APP_API_TIMEOUT) || 30000,
        });

        this.setupInterceptors();
    }

    /**
     * Setup axios interceptors for authentication and logging
     */
    setupInterceptors() {
        // Add request interceptor for logging and authentication
        this.api.interceptors.request.use(
            async (config) => {
                if (LOG_REQUESTS) {
                    console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, config.data);
                }
                
                // Add authentication token for non-mock requests
                if (!USE_MOCK_DATA && !config.url?.includes('/oauth/token')) {
                    try {
                        await this.ensureAuthenticated();
                        if (this.accessToken) {
                            config.headers.Authorization = `Bearer ${this.accessToken}`;
                        } else {
                            throw new Error('No access token available');
                        }
                    } catch (authError) {
                        console.error('[API Request] Authentication failed:', authError.message);
                        return Promise.reject(authError);
                    }
                }
                
                return config;
            },
            (error) => {
                if (DEBUG_API) {
                    console.error('[API Request Error]', error);
                }
                return Promise.reject(error);
            }
        );

        // Add response interceptor for logging
        this.api.interceptors.response.use(
            (response) => {
                if (LOG_REQUESTS) {
                    console.log(`[API Response] ${response.status}`, response.data);
                }
                return response;
            },
            async (error) => {
                if (DEBUG_API) {
                    console.error('[API Response Error]', error.response?.data || error.message);
                    // Log detailed validation errors if available
                    if (error.response?.data?.errors) {
                        console.error('[API Validation Errors]', error.response.data.errors);
                        error.response.data.errors.forEach((err, index) => {
                            console.error(`  Error ${index + 1}:`, err);
                        });
                    }
                }
                
                // Handle 401 errors by re-authenticating and retrying once
                if (error.response?.status === 401 && !USE_MOCK_DATA && !error.config._retry) {
                    console.warn('[API Response] 401 Unauthorized - attempting re-authentication');
                    
                    // Clear expired token
                    this.accessToken = null;
                    this.tokenExpiry = null;
                    this.isAuthenticating = false;
                    
                    // Mark this request as a retry to prevent infinite loops
                    error.config._retry = true;
                    
                    try {
                        // Re-authenticate
                        await this.ensureAuthenticated();
                        
                        // Update the authorization header
                        if (this.accessToken) {
                            error.config.headers.Authorization = `Bearer ${this.accessToken}`;
                        }
                        
                        // Retry the original request
                        return this.api.request(error.config);
                    } catch (retryError) {
                        console.error('[API Response] Re-authentication failed:', retryError.message);
                        return Promise.reject(retryError);
                    }
                }
                
                return Promise.reject(error);
            }
        );
    }

    /**
     * Ensure authentication token is valid
     */
    async ensureAuthenticated() {
        if (USE_MOCK_DATA) {
            return; // No authentication needed for mock data
        }

        // Check if token is still valid
        if (this.accessToken && this.tokenExpiry && Date.now() < this.tokenExpiry) {
            return;
        }

        // Prevent multiple simultaneous authentication attempts
        if (this.isAuthenticating) {
            // Wait for ongoing authentication
            while (this.isAuthenticating) {
                await new Promise(resolve => setTimeout(resolve, 100));
            }
            return;
        }

        await this.authenticate();
    }

    /**
     * Authenticate with Shopware 6 API
     */
    async authenticate() {
        if (USE_MOCK_DATA) {
            return;
        }

        this.isAuthenticating = true;

        try {
            if (DEBUG_API) {
                console.log('[Auth] Attempting authentication...');
            }

            const response = await axios.post(`${API_URL}/oauth/token`, {
                grant_type: 'client_credentials',
                client_id: OAUTH_CONFIG.clientId,
                client_secret: OAUTH_CONFIG.clientSecret,
                scope: 'write'
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            this.accessToken = response.data.access_token;
            this.tokenExpiry = Date.now() + (response.data.expires_in * 1000) - 60000; // 1 minute buffer

            if (DEBUG_API) {
                console.log('[Auth] Authentication successful');
            }
        } catch (error) {
            console.error('[Auth] Authentication failed:', error.response?.data || error.message);
            throw new Error(`Authentication failed: ${error.response?.data?.message || error.message}`);
        } finally {
            this.isAuthenticating = false;
        }
    }

    /**
     * Get customers with search filters
     */
    async getCustomers(searchTerm, limit = 25) {
        if (USE_MOCK_DATA) {
            return await mockDataService.getCustomers(searchTerm, limit);
        }
        
        try {
            const filters = [];
            
            if (searchTerm && searchTerm.trim()) {
                const term = searchTerm.trim();
                
                // Create OR filter for multiple fields
                filters.push({
                    type: 'multi',
                    operator: 'OR',
                    queries: [
                        { type: 'contains', field: 'customerNumber', value: term },
                        { type: 'contains', field: 'firstName', value: term },
                        { type: 'contains', field: 'lastName', value: term },
                        { type: 'contains', field: 'email', value: term },
                        { type: 'contains', field: 'company', value: term }
                    ]
                });
            }
            
            const response = await this.api.post('/search/customer', {
                filter: filters,
                limit: limit,
                sort: [{ field: 'customerNumber', order: 'ASC' }]
            });
            
            if (DEBUG_API) {
                console.log('[Customers] Search results:', response.data.total);
            }
            
            return {
                success: true,
                data: response.data.data || [],
                total: response.data.total || 0
            };
            
        } catch (error) {
            console.error('Error fetching customers:', error);
            return {
                success: false,
                data: [],
                total: 0,
                error: error.response?.data?.errors?.[0]?.detail || error.message
            };
        }
    }

    /**
     * Get customer details with group information
     */
    async getCustomerDetails(customerId) {
        if (USE_MOCK_DATA) {
            return await mockDataService.getCustomerDetails(customerId);
        }
        
        try {
            const response = await this.api.post('/search/customer', {
                filter: [
                    { type: 'equals', field: 'id', value: customerId }
                ],
                associations: {
                    group: {
                        associations: {
                            translations: {}
                        }
                    },
                    defaultBillingAddress: {},
                    defaultShippingAddress: {},
                    addresses: {
                        associations: {
                            country: {},
                            salutation: {}
                        }
                    }
                }
            });
            
            if (response.data.data && response.data.data.length > 0) {
                const customer = response.data.data[0];
                
                // Debug: Log customer data structure to console
                console.log('[BaseApiClient] Customer details response:', {
                    id: customer.id,
                    customerNumber: customer.customerNumber,
                    customerGroupId: customer.customerGroupId,
                    group: customer.group,
                    customerGroup: customer.customerGroup,
                    allKeys: Object.keys(customer),
                    // Show all properties that might contain group info
                    groupRelated: Object.keys(customer).filter(key => 
                        key.toLowerCase().includes('group')
                    ).reduce((obj, key) => {
                        obj[key] = customer[key];
                        return obj;
                    }, {})
                });
                
                return customer; // Return customer directly
            } else {
                throw new Error('Customer not found');
            }
            
        } catch (error) {
            console.error('Error fetching customer details:', error);
            return null;
        }
    }

    /**
     * Get customer addresses
     */
    async getCustomerAddresses(customerId) {
        if (USE_MOCK_DATA) {
            return await mockDataService.getCustomerAddresses(customerId);
        }
        
        try {
            // Get customer details which includes addresses
            const customerDetails = await this.getCustomerDetails(customerId);
            
            if (customerDetails && customerDetails.addresses) {
                return customerDetails.addresses;
            } else {
                console.warn('No addresses found for customer:', customerId);
                return [];
            }
            
        } catch (error) {
            console.error('Error fetching customer addresses:', error);
            return [];
        }
    }

    /**
     * Get sales channels
     */
    async getSalesChannels() {
        if (USE_MOCK_DATA) {
            return await mockDataService.getSalesChannels();
        }
        
        try {
            const response = await this.api.get('/sales-channel');
            
            // Sort channels alphabetically by name
            const sortedChannels = (response.data.data || []).sort((a, b) => 
                (a.name || '').localeCompare(b.name || '', 'de', { sensitivity: 'base' })
            );
            
            if (DEBUG_API) {
                console.log('[Sales Channels] Loaded channels:', sortedChannels.length);
            }
            
            return {
                success: true,
                data: sortedChannels,
                total: sortedChannels.length
            };
        } catch (error) {
            console.error('Error fetching sales channels:', error);
            return {
                success: false,
                data: [],
                total: 0,
                error: error.response?.data?.errors?.[0]?.detail || error.message
            };
        }
    }

    /**
     * Get payment methods
     */
    async getPaymentMethods() {
        if (USE_MOCK_DATA) {
            return await mockDataService.getPaymentMethods();
        }
        
        try {
            const response = await this.api.get('/payment-method');
            
            // Sort payment methods alphabetically by name
            const sortedMethods = (response.data.data || []).sort((a, b) => 
                (a.name || '').localeCompare(b.name || '', 'de', { sensitivity: 'base' })
            );
            
            if (DEBUG_API) {
                console.log('[Payment Methods] Loaded methods:', sortedMethods.length);
            }
            
            return {
                success: true,
                data: sortedMethods,
                total: sortedMethods.length
            };
        } catch (error) {
            console.error('Error fetching payment methods:', error);
            return {
                success: false,
                data: [],
                total: 0,
                error: error.response?.data?.errors?.[0]?.detail || error.message
            };
        }
    }

    /**
     * Get shipping methods
     */
    async getShippingMethods() {
        if (USE_MOCK_DATA) {
            return await mockDataService.getShippingMethods();
        }
        
        try {
            const response = await this.api.get('/shipping-method');
            
            // Sort shipping methods alphabetically by name
            const sortedMethods = (response.data.data || []).sort((a, b) => 
                (a.name || '').localeCompare(b.name || '', 'de', { sensitivity: 'base' })
            );
            
            if (DEBUG_API) {
                console.log('[Shipping Methods] Loaded methods:', sortedMethods.length);
            }
            
            return {
                success: true,
                data: sortedMethods,
                total: sortedMethods.length
            };
        } catch (error) {
            console.error('Error fetching shipping methods:', error);
            return {
                success: false,
                data: [],
                total: 0,
                error: error.response?.data?.errors?.[0]?.detail || error.message
            };
        }
    }

    /**
     * Get countries
     */
    async getCountries() {
        if (USE_MOCK_DATA) {
            return await mockDataService.getCountries();
        }
        
        try {
            const response = await this.api.get('/country');
            
            // Sort countries alphabetically by name
            const sortedCountries = (response.data.data || []).sort((a, b) => 
                (a.name || '').localeCompare(b.name || '', 'de', { sensitivity: 'base' })
            );
            
            if (DEBUG_API) {
                console.log('[Countries] Loaded countries:', sortedCountries.length);
            }
            
            return {
                success: true,
                data: sortedCountries,
                total: sortedCountries.length
            };
        } catch (error) {
            console.error('Error fetching countries:', error);
            return {
                success: false,
                data: [],
                total: 0,
                error: error.response?.data?.errors?.[0]?.detail || error.message
            };
        }
    }

    /**
     * Find products by numbers with pricing associations
     */
    async findProductsByNumbers(productNumbers) {
        if (!productNumbers || productNumbers.length === 0) return [];
        
        try {
            const response = await this.api.post('/search/product', {
                filter: [
                    {
                        type: 'equalsAny',
                        field: 'productNumber',
                        value: productNumbers
                    }
                ],
                associations: {
                    prices: {}
                }
            });
            
            return response.data?.data || [];
        } catch (error) {
            console.error('Error finding products:', error);
            return [];
        }
    }

    /**
     * Create order via API
     */
    async createOrder(orderPayload) {
        if (USE_MOCK_DATA) {
            return await mockDataService.createOrder(orderPayload);
        }
        
        try {
            if (DEBUG_API) {
                console.log('[API] Creating order with payload:', JSON.stringify(orderPayload, null, 2));
                console.log('[API] Payload type checks:');
                console.log('- lineItems is array:', Array.isArray(orderPayload.lineItems));
                console.log('- addresses is array:', Array.isArray(orderPayload.addresses));
                console.log('- deliveries is array:', Array.isArray(orderPayload.deliveries));
                console.log('- transactions is array:', Array.isArray(orderPayload.transactions));
                console.log('- tags is array:', Array.isArray(orderPayload.tags));
            }
            
            const response = await this.api.post('/order', orderPayload);
            
            // Handle 204 No Content response (successful creation but no data returned)
            if (response.status === 204) {
                return {
                    success: true,
                    data: null,
                    message: 'Order created successfully'
                };
            }
            
            return {
                success: true,
                data: response.data?.data || response.data,
                message: 'Order created successfully'
            };
        } catch (error) {
            console.error('Error creating order:', error);
            console.error('Full error response:', error.response?.data);
            return {
                success: false,
                error: error.response?.data?.errors?.[0]?.detail || error.message,
                errorData: error.response?.data
            };
        }
    }
}

export default BaseApiClient;